import { cast, flow, SnapshotIn, types } from 'mobx-state-tree'
import { CardApi } from '../../services/api-objects/CardApi'

const CardVersion = types.model('CardVersion').props({
  card_json: types.frozen(),
  language: types.string,
})

export const InfoCard = types
  .model('InfoCard')
  .props({
    status: types.optional(types.enumeration(['idle', 'pending', 'done', 'error']), 'idle'),
    id: types.identifierNumber,
    card_id: types.maybe(types.number),
    card_name: types.maybe(types.string),
    card_json: types.frozen(),
    card_markdown: types.frozen(),
    order: types.maybe(types.maybeNull(types.number)),
    versions: types.array(CardVersion),
    approved: types.maybe(types.boolean),
    touched: types.maybe(types.boolean),
    showStatus: types.maybe(types.boolean),
    start_time: types.maybe(types.string),
    end_time: types.maybe(types.string),
    is_empty: types.maybe(types.boolean),
  })
  .actions((self) => ({
    set<K extends keyof SnapshotIn<typeof self>, T extends SnapshotIn<typeof self>>(
      key: K,
      value: T[K],
    ) {
      self[key] = cast(value)
    },
    setEmpty(is_empty: boolean) {
      self.is_empty = is_empty
    },
    setStartTime(time) {
      self.start_time = time
    },
    setEndTime(time) {
      self.end_time = time
    },
    setCardName(name: string) {
      self.card_name = name
    },
    setCardJSON(json) {
      self.card_json = json
    },
    setApproved(approved) {
      self.approved = approved
    },
    setTouched(touched) {
      self.touched = touched
    },
    setStatus(value?: 'idle' | 'pending' | 'done' | 'error') {
      self.status = value || 'idle'
    },
  }))
  .views((self) => ({
    get new() {
      return self.card_id && self.id === null
    },
    get formErrors() {
      const errors: any = {}
      if (self.is_empty) {
        errors.empty = {
          type: 'error',
          message: 'Info cards must have content to publish.',
        }
      }

      if (self.card_name && self.card_name.length > 60) {
        errors.text = {
          type: 'error',
          message: 'Card name must be 60 characters or less.',
        }
      }

      if (!self.card_name || self.card_name?.length === 0 || !self?.card_name?.trim().length) {
        errors.text = {
          type: 'error',
          message: 'Card name cannot be blank.',
        }
      }

      return errors
    },
    get formWarnings() {
      return []
    },
    get allErrors() {
      return [...Object.values(this.formErrors)]
    },
    get allWarnings() {
      return []
    },
  }))
  .actions((self) => ({
    update: flow(function* () {
      try {
        self.setStatus('pending')
        const result: Awaited<ReturnType<typeof CardApi.updateInfoCard>> =
          yield CardApi.updateInfoCard({
            id: self.id,
            card_name: self.card_name,
            versions: [{ card_json: self.card_json, language: 'en' }],
          })
        if (result.ok && result.data) {
          self.setStatus('done')
        }
      } catch (error) {
        self.setStatus('error')
        return false
      }
    }),
  }))
